/**
 * Unified Document URL Helper
 * 
 * Single source of truth for generating document URLs across the application.
 * Handles both development (local media files) and production (S3 presigned URLs).
 * 
 * USAGE:
 * import { getDocumentUrl, isPDF, getDocumentDownloadUrl } from '@/utils/documentUrlHelper';
 * 
 * const url = getDocumentUrl(document);
 * const downloadUrl = getDocumentDownloadUrl(document);
 * const isAPdf = isPDF(document);
 */

/**
 * PDF viewer configuration constants
 * Standardized across all components
 */
export const UI_CONSTANTS = {
  PDF_CONFIG: {
    toolbar: 1,      // Show PDF toolbar
    navpanes: 0,     // Hide navigation panes
    scrollbar: 1,    // Show scrollbar
    view: 'FitH'     // Fit to width
  }
};

/**
 * Generate a document URL for viewing
 * 
 * Priority Order:
 * 1. file_url (presigned S3 URL from backend) - PRODUCTION
 * 2. url property (legacy support)
 * 3. file path (local media) - DEVELOPMENT ONLY
 * 
 * @param {Object} doc - Document object from API with properties:
 *   - file_url: Presigned S3 URL (preferred)
 *   - url: Legacy URL property
 *   - file: File path (for local development)
 *   - name: Document name (for PDF detection)
 * @param {Object} options - Optional configuration
 *   - addPdfParams: Add PDF viewer parameters (default: true)
 * @returns {string} Document URL or '#' if unavailable
 */
export function getDocumentUrl(doc, options = {}) {
  if (!doc) {
    console.warn('⚠️ getDocumentUrl called with null/undefined document');
    return '#';
  }
  
  const { addPdfParams = true } = options;
  
  // PRIORITY 1: Use file_url (presigned S3 URL from backend)
  // This is the ONLY way to access S3 files in production
  if (doc.file_url && typeof doc.file_url === 'string') {
    // Full HTTP(S) URL - presigned S3 URL
    if (doc.file_url.startsWith('http://') || doc.file_url.startsWith('https://')) {
      console.log('✅ Using presigned S3 URL from file_url:', doc.file_url.substring(0, 100) + '...');
      return addPdfParams && isPDF(doc) 
        ? addPdfViewerParams(doc.file_url)
        : doc.file_url;
    }
    
    // Relative /media/ URL (development only)
    if (doc.file_url.startsWith('/media/')) {
      const baseUrl = getBaseUrl();
      const fullUrl = `${baseUrl}${doc.file_url}`;
      console.log('✅ Using local media URL from file_url:', fullUrl);
      return addPdfParams && isPDF(doc)
        ? addPdfViewerParams(fullUrl)
        : fullUrl;
    }
  }
  
  // PRIORITY 2: Use url property (legacy support)
  if (doc.url && typeof doc.url === 'string') {
    if (doc.url.startsWith('http')) {
      console.log('✅ Using legacy url property:', doc.url.substring(0, 100) + '...');
      return addPdfParams && isPDF(doc)
        ? addPdfViewerParams(doc.url)
        : doc.url;
    }
  }
  
  // PRIORITY 3: Construct from file path (development fallback ONLY)
  if (doc.file && typeof doc.file === 'string') {
    // CRITICAL: Don't try to construct URLs from S3 paths
    // S3 files MUST use presigned URLs - direct S3 URLs will return 403 Forbidden
    if (doc.file.includes('amazonaws.com') || doc.file.includes('s3.')) {
      console.error('❌ Document has S3 file path but no presigned URL. Backend error?', {
        id: doc.id,
        name: doc.name,
        file: doc.file,
        file_url: doc.file_url
      });
      return '#';
    }
    
    // Only construct /media/ URLs in development
    // Production MUST use presigned URLs from file_url
    const isDevelopment = process.env.NODE_ENV === 'development' || 
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';
    
    if (isDevelopment) {
      const baseUrl = getBaseUrl();
      let fullUrl;

      if (doc.file.startsWith('http://') || doc.file.startsWith('https://')) {
        // Already an absolute URL — use as-is
        fullUrl = doc.file;
      } else if (doc.file.startsWith('/media/')) {
        // Relative path with /media/ prefix — prepend baseUrl only
        fullUrl = `${baseUrl}${doc.file}`;
      } else {
        // Bare path like "documents/file.pdf" or "file.pdf"
        let filePath = doc.file.replace(/^(media|documents)\//, '');
        if (!filePath.includes('/')) {
          filePath = `documents/${filePath}`;
        }
        fullUrl = `${baseUrl}/media/${filePath}`;
      }

      console.log('✅ Using constructed local media URL:', fullUrl);
      return addPdfParams && isPDF(doc)
        ? addPdfViewerParams(fullUrl)
        : fullUrl;
    }
    
    // In production, we MUST have presigned URLs - don't construct media URLs
    console.error('❌ Production environment but no presigned URL available', {
      id: doc.id,
      name: doc.name,
      file: doc.file,
      file_url: doc.file_url,
      environment: process.env.NODE_ENV,
      hostname: window.location.hostname
    });
    return '#';
  }
  
  // No valid URL source found
  console.warn('⚠️ No valid URL found for document', {
    id: doc.id,
    name: doc.name,
    hasFile: !!doc.file,
    hasFileUrl: !!doc.file_url,
    hasUrl: !!doc.url
  });
  return '#';
}

/**
 * Check if a document is a PDF file
 * 
 * @param {Object} doc - Document object with name/file_name/file property
 * @returns {boolean} True if document is a PDF
 */
export function isPDF(doc) {
  if (!doc) return false;
  
  // Check various name properties
  const name = doc.name || doc.file_name || doc.file || '';
  return name.toLowerCase().endsWith('.pdf');
}

/**
 * Add PDF viewer parameters to a URL
 * Standardizes PDF viewing experience across all components
 * 
 * @param {string} url - Base URL
 * @returns {string} URL with PDF viewer parameters
 */
function addPdfViewerParams(url) {
  if (!url || url === '#') return url;
  
  try {
    // Try to parse as URL object for proper handling
    const urlObj = new URL(url);
    urlObj.hash = `toolbar=${UI_CONSTANTS.PDF_CONFIG.toolbar}&navpanes=${UI_CONSTANTS.PDF_CONFIG.navpanes}&scrollbar=${UI_CONSTANTS.PDF_CONFIG.scrollbar}&view=${UI_CONSTANTS.PDF_CONFIG.view}`;
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails (shouldn't happen), append as string
    console.warn('⚠️ Could not parse URL for PDF params, appending as string:', url);
    return `${url}#toolbar=${UI_CONSTANTS.PDF_CONFIG.toolbar}&navpanes=${UI_CONSTANTS.PDF_CONFIG.navpanes}&scrollbar=${UI_CONSTANTS.PDF_CONFIG.scrollbar}&view=${UI_CONSTANTS.PDF_CONFIG.view}`;
  }
}

/**
 * Get base URL for API/media requests
 * Removes /api suffix if present
 * 
 * @returns {string} Base URL
 */
function getBaseUrl() {
  const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
  // Remove /api suffix if present (media files are served from root)
  return apiUrl.replace(/\/api\/?$/, '');
}

/**
 * Get download URL for a document
 * Uses dedicated download endpoint that sets proper Content-Disposition headers
 * 
 * NOTE: This returns a URL that requires authentication. For direct link downloads,
 * use downloadDocument() instead which handles auth properly.
 * 
 * @param {Object} doc - Document object with id property
 * @returns {string} Download URL or '#' if unavailable
 */
export function getDocumentDownloadUrl(doc) {
  if (!doc || !doc.id) {
    console.warn('⚠️ getDocumentDownloadUrl called without document id');
    return '#';
  }
  
  const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000/api';
  return `${baseUrl}/documents/${doc.id}/download/`;
}

/**
 * Download a document with proper authentication
 * 
 * This function fetches the download endpoint with auth headers, which returns
 * a redirect to a presigned S3 URL with Content-Disposition: attachment.
 * 
 * @param {Object} doc - Document object with id property
 * @param {Object} api - Axios API instance with auth configured
 * @returns {Promise<void>}
 */
export async function downloadDocument(doc, api) {
  if (!doc || !doc.id) {
    console.warn('⚠️ downloadDocument called without document id');
    return;
  }
  
  try {
    console.log(`📥 Initiating download for document ${doc.id}: ${doc.name}`);
    
    // Use the API to fetch the download endpoint - this will follow the redirect
    // and return the presigned URL or file content
    const response = await api.get(`/documents/${doc.id}/download/`, {
      responseType: 'blob',
      maxRedirects: 5  // Allow following redirects to S3
    });
    
    // Create a blob URL and trigger download
    const blob = new Blob([response.data], { 
      type: response.headers['content-type'] || 'application/octet-stream' 
    });
    const url = window.URL.createObjectURL(blob);
    
    // Create temporary link and click it
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.name || 'document';
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log(`✅ Download completed for document ${doc.id}`);
  } catch (error) {
    console.error(`❌ Download failed for document ${doc.id}:`, error);
    
    // Fallback: Try opening the presigned URL directly if available
    if (doc.file_url) {
      console.log('📥 Falling back to direct presigned URL download');
      window.open(doc.file_url, '_blank');
    } else {
      throw error;
    }
  }
}

/**
 * Validate that a document has a viewable URL
 * Useful for conditional rendering
 * 
 * @param {Object} doc - Document object
 * @returns {boolean} True if document has a valid URL
 */
export function hasValidUrl(doc) {
  const url = getDocumentUrl(doc);
  return url !== '#';
}

/**
 * Get document type icon class
 * Returns Font Awesome icon class based on file type
 * 
 * @param {Object} doc - Document object
 * @returns {string} Font Awesome icon class
 */
export function getDocumentIcon(doc) {
  if (!doc) return 'fas fa-file';
  
  const name = (doc.name || doc.file_name || doc.file || '').toLowerCase();
  
  if (name.endsWith('.pdf')) return 'fas fa-file-pdf';
  if (name.endsWith('.doc') || name.endsWith('.docx')) return 'fas fa-file-word';
  if (name.endsWith('.xls') || name.endsWith('.xlsx')) return 'fas fa-file-excel';
  if (name.endsWith('.txt')) return 'fas fa-file-alt';
  if (name.endsWith('.zip') || name.endsWith('.rar')) return 'fas fa-file-archive';
  if (name.match(/\.(jpg|jpeg|png|gif|bmp)$/)) return 'fas fa-file-image';
  
  return 'fas fa-file';
}

// Export all functions and constants
export default {
  getDocumentUrl,
  isPDF,
  getDocumentDownloadUrl,
  downloadDocument,
  hasValidUrl,
  getDocumentIcon,
  UI_CONSTANTS
};

